import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '../../supabaseClient';
import { motion, AnimatePresence } from 'framer-motion';
import MetaData from '../../components/shared/MetaData';
import { 
 ShieldCheck, UserCircle, GraduationCap, 
 ArrowRight, Lock, Mail, Globe, 
 Briefcase, CheckCircle2, Eye, EyeOff
} from 'lucide-react';
import AdukulLogo from '../../components/shared/AdukulLogo';
import DecryptedText from '../../components/ui/DecryptedText';
import ShinyText from '../../components/ui/ShinyText';
import SpotlightCard from '../../components/ui/SpotlightCard';


const LoginPage = () => {
 const navigate = useNavigate();
 const [loading, setLoading] = useState(false);
 const [checkingSession, setCheckingSession] = useState(true); // ✅ New state
 const [selectedRole, setSelectedRole] = useState<string | null>(null);
 const [showWelcome, setShowWelcome] = useState(false);
 const [showPassword, setShowPassword] = useState(false);
 
 const [loginData, setLoginData] = useState({
  school_code: '', 
  full_name: '',
  father_name: '',
  class_name: '',
  password: '',
  email: ''
 });

 // ✅ AUTO-REDIRECT LOGIC: Check if user is already logged in
 React.useEffect(() => {
  const checkSession = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      try {
        // Try to identify role from teachers table first
        const { data: staffData } = await supabase
          .from('teachers')
          .select('role, school_id')
          .eq('email', session.user.email)
          .maybeSingle();

        if (staffData) {
          // Fetch complete school metadata for branding
          const { data: school } = await supabase
            .from('schools')
            .select('name, logo_url, school_code')
            .eq('id', staffData.school_id)
            .maybeSingle();

          if (school) {
            localStorage.setItem('current_school_id', staffData.school_id);
            localStorage.setItem('current_school_name', school.name);
            localStorage.setItem('current_school_code', school.school_code);
            if (school.logo_url) {
              localStorage.setItem('current_school_logo', school.logo_url);
            }
          }
          navigate(`/${staffData.role}/dashboard`);
          return;
        }

        // Check if student
        const { data: studentData } = await supabase
          .from('students')
          .select('school_id')
          .eq('email', session.user.email)
          .maybeSingle();

        if (studentData) {
          const { data: school } = await supabase
            .from('schools')
            .select('name, logo_url, school_code')
            .eq('id', studentData.school_id)
            .maybeSingle();

          if (school) {
            localStorage.setItem('current_school_id', studentData.school_id);
            localStorage.setItem('current_school_name', school.name);
            localStorage.setItem('current_school_code', school.school_code);
            if (school.logo_url) localStorage.setItem('current_school_logo', school.logo_url);
          }
          navigate('/student/dashboard');
          return;
        }
      } catch (err) {
        console.error("Session check redirect failed:", err);
      }
    }
    setCheckingSession(false);
  };
  checkSession();
 }, [navigate]);

 const handleLogin = async (e: React.FormEvent) => {
  e.preventDefault();
  setLoading(true);

  try {
   // 1. Resolve School Code to School ID
   const { data: school, error: schoolError } = await supabase
    .from('schools')
    .select('id, name, logo_url, school_code')
    .ilike('school_code', loginData.school_code.trim())
    .maybeSingle();

   if (schoolError || !school) {
    throw new Error("Invalid School Code! Please contact your administrator.");
   }

   if (selectedRole === 'student') {
    const { data: studentRecord, error: dbError } = await supabase
     .from('students')
     .select('student_id, full_name, father_name, class_name, email, is_approved')
     .eq('school_id', school.id) // ✅ Filter by school
     .ilike('full_name', `%${loginData.full_name.trim()}%`)
     .ilike('father_name', `%${loginData.father_name.trim()}%`)
     .ilike('class_name', `%${loginData.class_name.trim()}%`)
     .limit(1)
     .maybeSingle();

    if (dbError) throw dbError;
    if (!studentRecord) {
     toast.error("Student record not found in this school!");
     setLoading(false);
     return;
    }

    if (studentRecord.is_approved !== 'approved') {
     toast.error("⏳ Account Approval Pending!");
     setLoading(false);
     return;
    }
    
    // Student static password for simple sessions
    const { error: authError } = await supabase.auth.signInWithPassword({
      email: studentRecord.email,
      password: 'Student123'
    });
    
    if (authError) throw authError;

    // Store school context
    localStorage.setItem('current_school_id', school.id);
    localStorage.setItem('current_school_name', school.name);
    localStorage.setItem('current_school_code', school.school_code);
    if (school.logo_url) localStorage.setItem('current_school_logo', school.logo_url);

    setShowWelcome(true);
    setTimeout(() => navigate('/student/dashboard'), 2000);
   } 
   else {
    const { error: authError } = await supabase.auth.signInWithPassword({
     email: loginData.email,
     password: loginData.password,
    });

    if (authError) throw authError;

    const { data: staffRecord } = await supabase
     .from('teachers')
     .select('role, school_id')
     .eq('email', loginData.email.trim())
     .limit(1)
     .maybeSingle();

    if (!staffRecord || staffRecord.role !== selectedRole) {
     await supabase.auth.signOut();
     throw new Error(`Unauthorized: Role mismatch.`);
    }

    if (staffRecord.school_id !== school.id) {
     await supabase.auth.signOut();
     throw new Error(`Unauthorized: You are not registered with ${school.name}.`);
    }


    // Store school context
    localStorage.setItem('current_school_id', school.id);
    localStorage.setItem('current_school_name', school.name);
    localStorage.setItem('current_school_code', school.school_code);
    if (school.logo_url) {
      localStorage.setItem('current_school_logo', school.logo_url);
    }

    setShowWelcome(true);
    setTimeout(() => navigate(`/${selectedRole === 'admin' ? 'admin' : 'teacher'}/dashboard`), 2000);
   }

  } catch (error: any) {
   toast.error(error.message || "Authentication failed");
  } finally {
   setLoading(false);
  }
 };

 const roles = [
  { id: 'admin', icon: <ShieldCheck size={40} />, label: 'Administration', sub: 'Control Center', color: 'bg-blue-600' },
  { id: 'teacher', icon: <Briefcase size={40} />, label: 'Teacher', sub: 'Educator Hub', color: 'bg-emerald-600' },
  { id: 'student', icon: <GraduationCap size={40} />, label: 'Student', sub: 'Learning Portal', color: 'bg-purple-600' }
 ];

  if (checkingSession) return (
    <div className="min-h-screen bg-[#f9fafb] flex items-center justify-center p-6 font-inter">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-slate-800 border-t-slate-300 rounded-full animate-spin"></div>
        <p className="text-[10px] font-black text-slate-400 tracking-widest uppercase">Authenticating Protocol...</p>
      </div>
    </div>
  );

  return (
   <div className="min-h-screen bg-[#f9fafb] flex items-center justify-center p-6 relative overflow-hidden font-inter">
    <MetaData title="Login to Adukul" description="Access your school dashboard, manage students, fees, and more." />
    {/* Background Subtle Grid */}
    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.02] pointer-events-none"></div>

   <AnimatePresence mode="wait">
    {showWelcome ? (
     <motion.div 
      key="success"
      initial={{ opacity: 0, scale: 0.9 }} 
      animate={{ opacity: 1, scale: 1 }}
      className="text-center"
     >
      <div className={`w-24 h-24 rounded-[5px] flex items-center justify-center mx-auto mb-10 shadow-3xl text-white ${
       selectedRole === 'admin' ? 'bg-blue-600' : selectedRole === 'teacher' ? 'bg-emerald-600' : 'bg-purple-600'
      }`}>
       <CheckCircle2 size={48} />
      </div>
      <h2 className="text-5xl font-black text-slate-800  mb-4 uppercase">Identity<br/>Verified</h2>
      <div className="flex items-center justify-center gap-2">
       <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></div>
       <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce [animation-delay:0.2s]"></div>
       <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce [animation-delay:0.4s]"></div>
      </div>
     </motion.div>
    ) : !selectedRole ? (
      <motion.div 
       key="selector"
       initial={{ opacity: 0, y: 20 }} 
       animate={{ opacity: 1, y: 0 }} 
       className="w-full max-w-full"
      >
       <div className="text-center mb-16">
        <div className="mx-auto mb-8 flex flex-col items-center">
         <img src="/logo.png" className="w-32 h-32 object-contain hover:rotate-6 transition-transform" alt="Logo" />
         <h1 className="text-6xl md:text-7xl font-black text-slate-900 leading-[0.9] mt-8 uppercase flex flex-col items-center gap-2">
           <DecryptedText 
             text="ADUKUL DIGITAL" 
             animateOn="view"
             speed={40}
             className="text-slate-900"
             encryptedClassName="text-blue-600 font-mono"
           />
           <DecryptedText 
             text="ECOSYSTEM" 
             animateOn="view"
             speed={50}
             className="text-slate-400"
             encryptedClassName="text-emerald-600 font-mono"
           />
         </h1>
        </div>
        <p className="text-slate-500 font-black text-[10px] flex items-center justify-center gap-2">
         <Globe size={14} className="text-blue-500" />
         <ShinyText 
           text="Advanced Institutional Intelligence Platform" 
           speed={2.5} 
           color="#64748b" 
           shineColor="#3b82f6" 
           className="tracking-widest uppercase"
         />
        </p>
       </div>

       <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {roles.map((role) => {
          const spotColors = {
            admin: 'rgba(37, 99, 235, 0.15)',
            teacher: 'rgba(16, 185, 129, 0.15)',
            student: 'rgba(139, 92, 246, 0.15)'
          };
          return (
           <motion.div 
            key={role.id}
            onClick={() => setSelectedRole(role.id)}
            whileHover={{ y: -6 }}
            className="cursor-pointer"
           >
            <SpotlightCard
              spotlightColor={spotColors[role.id as keyof typeof spotColors]}
              className="role-card h-full bg-white border border-slate-200/80 hover:border-slate-300 transition-colors shadow-sm"
            >
              <div className={`w-20 h-20 rounded-[5px] flex items-center justify-center text-white mb-6 ${role.color} shadow-lg`}>
               {role.icon}
              </div>
              <div>
               <h3 className="text-2xl font-black text-slate-800 uppercase">{role.label}</h3>
               <p className="text-xs font-black text-slate-400 tracking-widest mt-1">{role.sub}</p>
              </div>
              <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mt-6 transition-colors border border-slate-100">
               <ArrowRight size={20} className="text-slate-600" />
              </div>
            </SpotlightCard>
           </motion.div>
          );
        })}
       </div>

       <div className="mt-20 text-center opacity-40">
        <p className="text-[10px] font-black text-slate-400 ">Paid Access Protocol v4.0</p>
       </div>
      </motion.div>
     ) : (
      <motion.div 
       key="login"
       initial={{ opacity: 0, x: 20 }} 
       animate={{ opacity: 1, x: 0 }} 
       className="w-full max-w-md"
      >
        <button 
         onClick={() => setSelectedRole(null)}
         className="mb-8 flex items-center gap-2 text-[10px] font-black text-slate-400 tracking-widest hover:text-slate-900 transition-colors"
        >
         <ArrowRight size={14} className="rotate-180" /> Change Protocol
        </button>

        <SpotlightCard
          spotlightColor={
            selectedRole === 'admin' 
              ? 'rgba(37, 99, 235, 0.15)' 
              : selectedRole === 'teacher' 
                ? 'rgba(16, 185, 129, 0.15)' 
                : 'rgba(139, 92, 246, 0.15)'
          }
          className="p-10 bg-white border border-slate-200/80 shadow-2xl relative overflow-hidden rounded-3xl"
        >
         <div className={`absolute top-0 left-0 w-full h-2 ${
          selectedRole === 'admin' ? 'bg-blue-600' : selectedRole === 'teacher' ? 'bg-emerald-600' : 'bg-purple-600'
         }`}></div>
         
         <h2 className="text-4xl font-black text-slate-900 mb-2 uppercase flex flex-col items-start gap-1">
           <DecryptedText 
             text={selectedRole} 
             animateOn="view"
             speed={60}
             className="text-slate-900"
             encryptedClassName="text-slate-400 font-mono"
           />
           <span className="text-slate-400">Entry</span>
         </h2>
         <p className="text-[10px] font-black text-slate-400  mb-10">Verification required to proceed</p>

         <form onSubmit={handleLogin} className="space-y-6">
          {/* ✅ School Identifier Node */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Institutional Identifier</label>
            <input 
             type="text" 
             placeholder="School Code (Ex: ASM01)" 
             required 
             className="premium-input uppercase" 
             value={loginData.school_code}
             onChange={e => setLoginData({...loginData, school_code: e.target.value.toUpperCase()})} 
            />
          </div>

          {selectedRole === 'student' ? (
           <>
            <div className="space-y-1.5">
             <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Asset Nomenclature</label>
             <input type="text" placeholder="Full Student Name" required className="premium-input" 
              onChange={e => setLoginData({...loginData, full_name: e.target.value})} />
            </div>
            <div className="space-y-1.5">
             <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Patrilineal Reference</label>
             <input type="text" placeholder="Father's Name" required className="premium-input" 
              onChange={e => setLoginData({...loginData, father_name: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
             <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Archive ID</label>
              <input type="text" placeholder="Class (Ex: 10A)" required className="premium-input " 
               onChange={e => setLoginData({...loginData, class_name: e.target.value})} />
             </div>
             <div className="flex items-end">
               <button type="submit" disabled={loading} className="premium-button w-full text-white tracking-widest text-[10px] h-[46px] bg-purple-600">
                {loading ? '...' : 'Unlock'}
               </button>
             </div>
            </div>
           </>
          ) : (
           <>
            <div className="space-y-1.5">
             <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Identity Token</label>
             <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input type="email" placeholder="staff@institution.com" required className="premium-input pl-12" 
               onChange={e => setLoginData({...loginData, email: e.target.value})} />
             </div>
            </div>
            <div className="space-y-1.5">
             <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Access Cipher</label>
             <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input type={showPassword ? "text" : "password"} placeholder="••••••••" required className="premium-input pl-12 pr-12" 
               onChange={e => setLoginData({...loginData, password: e.target.value})} />
              <button 
               type="button"
               onClick={() => setShowPassword(!showPassword)}
               className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              >
               {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
             </div>
             <div className="flex justify-end pr-2">
              <button 
               type="button" 
               onClick={() => navigate('/reset-password')}
               className="text-[9px] font-black text-slate-400 hover:text-blue-600 transition-colors uppercase tracking-widest"
              >
               Forgot Password?
              </button>
             </div>
            </div>
            <button type="submit" disabled={loading} className={`premium-button w-full py-4 text-white  font-black shadow-lg hover:shadow-2xl active:scale-95 tracking-widest mt-4 ${
             selectedRole === 'admin' ? 'bg-blue-600 hover:bg-blue-700' : selectedRole === 'teacher' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-purple-600 hover:bg-purple-700'
            }`}>
              {loading ? 'Authenticating...' : 'Establish Link'}
            </button>
           </>
          )}
         </form>

         <div className="mt-8 pt-8 border-t border-slate-50 text-center opacity-30">
           <p className="text-[8px] font-black text-slate-500 ">Proprietary Educational </p>
         </div>
        </SpotlightCard>
      </motion.div>
     )}
   </AnimatePresence>
  </div>
 );
};

export default LoginPage;
